import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type {
  PaymentRequestClosureStatus,
  PaymentRequestCommentSource,
  PaymentRequestDocumentType,
  PaymentRequestReason,
  PaymentRequestRequestedAmountType,
  PaymentRequestStatus,
} from "../entities";

export function usePaymentRequestEnumTranslations() {
  const { t } = useTranslation("PaymentRequest");
  return useMemo(() => {
    const paymentRequestStatus: Record<PaymentRequestStatus, string> = {
      New: t("Enum:PaymentRequestStatus.New"),
      Open: t("Enum:PaymentRequestStatus.Open"),
      Refused: t("Enum:PaymentRequestStatus.Refused"),
      PendingShop: t("Enum:PaymentRequestStatus.PendingShop"),
      PendingInsurer: t("Enum:PaymentRequestStatus.PendingInsurer"),
      Closed: t("Enum:PaymentRequestStatus.Closed"),
      PendingHO: t("Enum:PaymentRequestStatus.PendingHO"),
      TransferredHigherLevel: t(
        "Enum:PaymentRequestStatus.TransferredHigherLevel"
      ),
    };
    const paymentRequestReason: Record<PaymentRequestReason, string> = {
      Over: t("Enum:PaymentRequestReason.Over"),
      Missing: t("Enum:PaymentRequestReason.Missing"),
      Other: t("Enum:PaymentRequestReason.Other"),
      Short: t("Enum:PaymentRequestReason.Short"),
      UnableToApply: t("Enum:PaymentRequestReason.UnableToApply"),
    };
    const paymentRequestClosureStatus: Record<
      PaymentRequestClosureStatus,
      string
    > = {
      Resolved: t("Enum:PaymentRequestClosureStatus.Resolved"),
      NotClosed: t("Enum:PaymentRequestClosureStatus.NotClosed"),
      AlreadyPaid: t("Enum:PaymentRequestClosureStatus.AlreadyPaid"),
      NoAnswerFromShop: t("Enum:PaymentRequestClosureStatus.NoAnswerFromShop"),
      AlreadySubmitted: t("Enum:PaymentRequestClosureStatus.AlreadySubmitted"),
      InsurerWontPay: t("Enum:PaymentRequestClosureStatus.InsurerWontPay"),
      Unknown: t("Enum:PaymentRequestClosureStatus.Unknown"),
      Other: t("Enum:PaymentRequestClosureStatus.Other"),
      InTransit: t("Enum:PaymentRequestClosureStatus.InTransit"),
      Unresolved: t("Enum:PaymentRequestClosureStatus.Unresolved"),
    };
    const paymentRequestDocumentType: Record<
      PaymentRequestDocumentType,
      string
    > = {
      Other: t("Enum:PaymentRequestDocumentType.Other"),
      Estimate: t("Enum:PaymentRequestDocumentType.Estimate"),
      Invoice: t("Enum:PaymentRequestDocumentType.Invoice"),
      Approval: t("Enum:PaymentRequestDocumentType.Approval"),
      Authorization: t("Enum:PaymentRequestDocumentType.Authorization"),
      OriginalSignedInvoice: t(
        "Enum:PaymentRequestDocumentType.OriginalSignedInvoice"
      ),
      FinalBill: t("Enum:PaymentRequestDocumentType.FinalBill"),
      CompletedFile: t("Enum:PaymentRequestDocumentType.CompletedFile"),
    };
    const paymentRequestCommentSource: Record<
      PaymentRequestCommentSource,
      string
    > = {
      HO: t("Enum:PaymentRequestCommentSource.HO"),
      Shop: t("Enum:PaymentRequestCommentSource.Shop"),
    };
    const paymentRequestRequestedAmountType: Record<
      PaymentRequestRequestedAmountType,
      string
    > = {
      Other: t("Enum:PaymentRequestRequestedAmountType.Other"),
      Repair: t("Enum:PaymentRequestRequestedAmountType.Repair"),
      Rental: t("Enum:PaymentRequestRequestedAmountType.Rental"),
      TowBill: t("Enum:PaymentRequestRequestedAmountType.TowBill"),
      Supplement: t("Enum:PaymentRequestRequestedAmountType.Supplement"),
    };
    return {
      paymentRequestStatus,
      paymentRequestReason,
      paymentRequestClosureStatus,
      paymentRequestDocumentType,
      paymentRequestCommentSource,
      paymentRequestRequestedAmountType,
    };
  }, [t]);
}
